"use client";
import { useState } from "react";

const revenueOptions = [
  "Select one...",
  "Under $30k/month",
  "$30k–$50k/month",
  "$50k–$100k/month",
  "$100k–$250k/month",
  "$250k+/month",
];

const leadSourceOptions = [
  "Select one...",
  "Door knocking",
  "Paid ads (we run them)",
  "Paid ads (agency runs them)",
  "Referrals",
  "Canvassing teams",
  "Online leads / lead vendors",
  "Mix of the above",
];

const adSpendOptions = [
  "Not currently running ads",
  "Under $2,000/month",
  "$2k–$5k/month",
  "$5k–$15k/month",
  "$15k+/month",
];

const bottleneckOptions = [
  "Select one...",
  "Not enough leads coming in",
  "Leads come in but don't convert to appointments",
  "Setter team is inconsistent or burning out",
  "Pipeline is unpredictable week to week",
  "Owner is too involved in the sales process",
  "Other",
];

const setterOptions = [
  "Yes — in-house setters",
  "Yes — outsourced setters",
  "Manual follow-up (owner or closers)",
  "Mix of setter and manual",
  "No — we need a system for this",
];

export default function QualifyForm() {
  const [submitted, setSubmitted] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // TODO: Replace with GHL form endpoint or webhook
    // Example: POST to GHL form embed URL or Zapier/webhook
    setSubmitted(true);
    setTimeout(() => {
      const bookEl = document.getElementById("book");
      if (bookEl) bookEl.scrollIntoView({ behavior: "smooth" });
    }, 800);
  }

  if (submitted) {
    return (
      <section id="qualify" className="py-20 px-6 md:px-10 bg-[#F8F8F4]">
        <div className="max-w-xl mx-auto text-center">
          <div className="text-5xl mb-4">✅</div>
          <h3 className="text-2xl font-black text-gray-900 mb-3">Got it — reviewing your submission now.</h3>
          <p className="text-gray-500 mb-6">Scroll down to book your strategy call while we review your details.</p>
          <a href="#book" className="bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-md transition-all duration-200 inline-block px-8 py-4 text-base">
            Book Your Strategy Call →
          </a>
        </div>
      </section>
    );
  }

  return (
    <section id="qualify" className="py-20 px-6 md:px-10 bg-[#F8F8F4]">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="max-w-xl mx-auto text-center mb-10">
          <p className="text-xs font-bold tracking-widest uppercase text-amber-500 mb-3">Get Started</p>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight mb-4">
            See if your company is a fit
          </h2>
          <p className="text-gray-500 text-base">
            Answer a few quick questions. If it looks like a strong fit, we&apos;ll reach out to schedule your strategy call.
          </p>
        </div>

        {/* Form card */}
        <div className="max-w-2xl mx-auto bg-white border border-gray-200 rounded-2xl p-8 md:p-10 shadow-sm">
          {/* GHL EMBED PLACEHOLDER */}
          {/* 
            TO CONNECT GHL FORM:
            Option A — Replace this <form> entirely with:
              <div dangerouslySetInnerHTML={{ __html: `<YOUR_GHL_FORM_EMBED_CODE>` }} />
            Option B — Keep this form and POST to a GHL webhook or use GHL's form action URL
            Option C — Redirect on submit to a GHL hosted form page
          */}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <FormField label="First Name" name="firstName" type="text" placeholder="Your first name" required />
              <FormField label="Last Name" name="lastName" type="text" placeholder="Your last name" required />
            </div>
            <FormField label="Company Name" name="company" type="text" placeholder="Your solar company name" required />
            <FormField label="Work Email" name="email" type="email" placeholder="you@yourcompany.com" required />
            <FormField label="Phone Number" name="phone" type="tel" placeholder="(555) 000-0000" required />

            <SelectField label="Monthly Revenue Range" name="revenue" options={revenueOptions} required />
            <SelectField label="Current Primary Lead Source" name="leadSource" options={leadSourceOptions} required />
            <SelectField label="Approx. Monthly Ad Spend (if any)" name="adSpend" options={adSpendOptions} />
            <SelectField label="Biggest Bottleneck Right Now" name="bottleneck" options={bottleneckOptions} required />

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Are you currently using setters or manual follow-up?
              </label>
              <div className="space-y-2">
                {setterOptions.map((opt) => (
                  <label key={opt} className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="radio"
                      name="setterUsage"
                      value={opt}
                      className="w-4 h-4 accent-amber-500"
                    />
                    <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                      {opt}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-md transition-all duration-200 py-4 text-base mt-2"
            >
              Check My Fit →
            </button>

            <p className="text-xs text-gray-400 text-center mt-3">
              No spam. No obligation. We review every submission personally.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

function FormField({
  label,
  name,
  type,
  placeholder,
  required,
}: {
  label: string;
  name: string;
  type: string;
  placeholder: string;
  required?: boolean;
}) {
  return (
    <div>
      <label htmlFor={name} className="block text-sm font-semibold text-gray-700 mb-1.5">
        {label} {required && <span className="text-amber-500">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        placeholder={placeholder}
        required={required}
        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all"
      />
    </div>
  );
}

function SelectField({
  label,
  name,
  options,
  required,
}: {
  label: string;
  name: string;
  options: string[];
  required?: boolean;
}) {
  return (
    <div>
      <label htmlFor={name} className="block text-sm font-semibold text-gray-700 mb-1.5">
        {label} {required && <span className="text-amber-500">*</span>}
      </label>
      <select
        id={name}
        name={name}
        required={required}
        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 bg-white transition-all appearance-none"
      >
        {options.map((o) => (
          <option key={o} value={o === "Select one..." ? "" : o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
